import { useState, useEffect, useCallback } from 'react';
import { fleetApiGo, fleetApi } from '../api/axiosClient';
import dayjs from 'dayjs';
import { timezone } from '../utils/timeZone';

export const fetchAlertNotificationDocsSearch = async (payload) => {
  try {
    const response = await fleetApiGo.post(`/alert-notification-docs/search`, payload);
    return response;
  } catch (error) {
    console.error('Error fetching doc notifications:', error);
    throw error;
  }
};

export const fetchHistoryGpsNotifierSearch = async (payload, tz = null) => {
  try {
    const tzVal = tz !== null ? tz : typeof timezone === 'function' ? timezone() : Number(timezone) || 7;
    const response = await fleetApi.post(`/history-gps-notifier/search?tz=${tzVal}`, payload);
    return response;
  } catch (error) {
    console.error('Error fetching gps history notifier:', error);
    throw error;
  }
};

export const fetchGpsNotifierSearch = async (payload, tz = null) => {
  try {
    const tzVal = tz !== null ? tz : typeof timezone === 'function' ? timezone() : Number(timezone) || 7;
    const response = await fleetApi.post(`/gps-notifier/search?tz=${tzVal}`, payload);
    return response;
  } catch (error) {
    console.error('Error fetching gps notifier:', error);
    throw error;
  }
};

export function useAlertNotifications(companyId, isPollingEnabled = true) {
  const [alerts, setAlerts] = useState([]);
  const [docsAlerts, setDocsAlerts] = useState([]);

  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchAllNotifications = useCallback(async () => {
    if (!companyId) return;

    try {
      setIsError(false);
      const tzVal = typeof timezone === 'function' ? timezone() : Number(timezone) || 7;
      const dateNow = dayjs();
      const startTimeRange = dateNow.startOf('day').subtract(tzVal, 'hour').format('YYYY-MM-DDTHH:mm:ss[Z]');
      const finishTimeRange = dateNow.endOf('day').subtract(tzVal, 'hour').format('YYYY-MM-DDTHH:mm:ss[Z]');

      const gpsPayload = {
        paging: { length: 1000, start: 0 },
        columns: [
          {
            name: 'finish_time',
            logic_operator: 'range',
            value: startTimeRange,
            value1: finishTimeRange,
            operator: 'or'
          },
          {
            name: 'start_time',
            logic_operator: 'range',
            value: startTimeRange,
            value1: finishTimeRange,
            operator: 'or'
          }
        ]
      };

      const [alertRes, docsRes] = await Promise.all([
        fetchGpsNotifierSearch(gpsPayload, tzVal),
        fetchAlertNotificationDocsSearch({
          start: 0,
          length: 1000,
          notifType: 'all',
          companyId: String(companyId),
          notifName: '',
          name: ''
        })
      ]);

      setAlerts(alertRes.data || []);
      setDocsAlerts(docsRes?.data || docsRes || []);
      setLastUpdated(new Date());
    } catch {
      setIsError(true);
    }
  }, [companyId]);

  useEffect(() => {
    let intervalId = null;

    if (isPollingEnabled && companyId) {
      setIsLoading(true);
      fetchAllNotifications().finally(() => setIsLoading(false));

      intervalId = setInterval(() => {
        fetchAllNotifications();
      }, 60 * 1000);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [fetchAllNotifications, isPollingEnabled, companyId]);

  const mutate = () => {
    setIsLoading(true);
    fetchAllNotifications().finally(() => setIsLoading(false));
  };

  return {
    alerts,
    docsAlerts,
    totalUnread: alerts.length + docsAlerts.length,
    isLoading,
    isError,
    lastUpdated,
    mutate
  };
}
